import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingBag, MessageCircle, ShieldCheck, Truck, Sparkles, Leaf,
  ChevronRight, Clock, ArrowRight, BadgeCheck, Star,
} from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { StarRating } from "@/components/StarRating";
import { waLink, STORE } from "@/config";

const KASHMIR = "https://images.unsplash.com/photo-1621232082074-1a7750ecc557?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const TRUST = [
  { icon: BadgeCheck, label: "Premium Quality", sub: "Hand-graded" },
  { icon: Leaf, label: "Freshly Packed", sub: "Hygienically sealed" },
  { icon: Star, label: "100% Satisfaction", sub: "Loved by 5000+" },
  { icon: Truck, label: "Pan-India Delivery", sub: "Fast & tracked" },
];

const OFFER_TABS = [
  { key: "bestseller", label: "Best Sellers" },
  { key: "kashmir-special", label: "Kashmir Special" },
  { key: "deal", label: "Today's Deals" },
  { key: "combo", label: "Combo Offers" },
  { key: "giftbox", label: "Gift Boxes" },
  { key: "festive", label: "Festive Offers" },
];

const useCountdown = () => {
  const [t, setT] = useState("");
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const diff = end - now;
      const h = String(Math.floor(diff / 3.6e6)).padStart(2, "0");
      const m = String(Math.floor((diff % 3.6e6) / 6e4)).padStart(2, "0");
      const s = String(Math.floor((diff % 6e4) / 1000)).padStart(2, "0");
      setT(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return t;
};

export default function Home() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState("bestseller");
  const [tabProducts, setTabProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const countdown = useCountdown();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/products", { params: { badge: "deal" } }).then((r) => setDeals(r.data.slice(0, 4)));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  useEffect(() => {
    api.get("/products", { params: { badge: tab } }).then((r) => setTabProducts(r.data.slice(0, 8)));
  }, [tab]);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={KASHMIR} alt="Kashmir valley" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-28 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-4 py-1.5 text-xs font-semibold text-[#FDE68A] uppercase tracking-widest">
              <Sparkles size={14} /> Authentic Kashmiri Since Generations
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mt-5">
              Premium Kashmiri Dry Fruits, <span className="text-gradient-gold">Delivered Fresh</span> to Your Door
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed">
              Authentic taste from Kashmir — carefully selected, hygienically packed and delivered across India.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => navigate("/shop")}
                data-testid="hero-shop-now-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold px-7 py-3.5 pill-shadow transition-colors"
              >
                <ShoppingBag size={19} /> Shop Now
              </button>
              <a
                href={waLink("Hi! I'd like to order premium Kashmiri dry fruits.")}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:brightness-95 text-white font-semibold px-7 py-3.5 transition"
              >
                <MessageCircle size={19} /> Order on WhatsApp
              </a>
            </div>
            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
              <span className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-[#FDE68A]" /> Premium Quality</span>
              <span className="flex items-center gap-1.5"><Leaf size={16} className="text-[#FDE68A]" /> Freshly Packed</span>
              <span className="flex items-center gap-1.5"><Star size={16} className="text-[#FDE68A]" /> 100% Satisfaction</span>
              <span className="flex items-center gap-1.5"><Truck size={16} className="text-[#FDE68A]" /> Pan-India Delivery</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="bg-[#F3ECE2] border-y border-[#E7E0D6]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {TRUST.map((t) => (
            <div key={t.label} className="flex items-center gap-3">
              <div className="h-11 w-11 shrink-0 rounded-full bg-white grid place-items-center text-[#C2410C] shadow-sm">
                <t.icon size={20} />
              </div>
              <div>
                <p className="font-semibold text-sm text-[#1F130B] leading-tight">{t.label}</p>
                <p className="text-xs text-[#6B5B52]">{t.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C2410C] font-semibold">Explore</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1F130B]">Shop by Category</h2>
          </div>
          <Link to="/categories" className="text-sm font-semibold text-[#C2410C] hidden sm:flex items-center gap-1 hover:gap-2 transition-all">View all <ChevronRight size={16} /></Link>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x">
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => navigate(`/shop?category=${c.slug}`)}
              data-testid={`home-category-${c.slug}`}
              className="group shrink-0 w-32 sm:w-40 snap-start"
            >
              <div className="aspect-square rounded-2xl overflow-hidden border border-[#E7E0D6] bg-white">
                <img src={c.image} alt={c.label} loading="lazy" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" />
              </div>
              <p className="mt-2 text-sm font-semibold text-[#1F130B] text-center leading-tight">{c.label}</p>
            </button>
          ))}
        </div>
      </section>

      {/* TODAY'S DEALS - dark luxury */}
      <section className="dark-luxury">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#D97706] font-semibold flex items-center gap-2"><Clock size={14} /> Limited Time</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-1">Today's Best Deals</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#B9A794]">Ends in</span>
              <div className="flex gap-1.5">
                {countdown.split(":").map((v, i) => (
                  <span key={i} className="rounded-lg bg-white/10 border border-[#D97706]/40 text-[#FDE68A] font-mono font-bold px-3 py-1.5 text-lg">{v}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {deals.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      </section>

      {/* OFFER TABS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-widest text-[#C2410C] font-semibold">Handpicked For You</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1F130B] mt-1">Special Collections</h2>
        </div>
        <div className="flex flex-wrap justify-center gap-2.5 mb-9">
          {OFFER_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`offer-tab-${t.key}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${
                tab === t.key
                  ? "bg-[#C2410C] text-white border-[#C2410C]"
                  : "bg-white text-[#6B5B52] border-[#E7E0D6] hover:border-[#C2410C]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tabProducts.length === 0 ? (
          <p className="text-center text-[#6B5B52]">Fresh stock coming soon in this collection.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tabProducts.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        )}
        <div className="text-center mt-9">
          <button onClick={() => navigate("/shop")} data-testid="home-view-all-products" className="inline-flex items-center gap-2 rounded-full border-2 border-[#1C120C] text-[#1C120C] font-semibold px-7 py-3 hover:bg-[#1C120C] hover:text-white transition-colors">
            View All Products <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="bg-[#F3ECE2]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={KASHMIR} alt="Kashmir sourcing" className="rounded-3xl w-full h-80 object-cover" />
            <div className="absolute -bottom-6 -right-4 sm:right-6 bg-white rounded-2xl shadow-xl px-6 py-4 border border-[#E7E0D6]">
              <p className="font-display text-3xl font-bold text-[#C2410C]">25+</p>
              <p className="text-xs text-[#6B5B52] font-medium">Years of Trust</p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C2410C] font-semibold">Our Story</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1F130B] mt-1">About Sonia Dry Fruits Jammu</h2>
            <p className="mt-4 text-[#6B5B52] leading-relaxed">
              For generations, Sonia Dry Fruits has been sourcing the finest nuts, saffron and dry fruits straight from the orchards and valleys of Kashmir. Every walnut, almond and thread of saffron is hand-selected for purity, freshness and taste.
            </p>
            <p className="mt-3 text-[#6B5B52] leading-relaxed">
              We hygienically pack every order and deliver it fresh across India — bringing the authentic taste of Kashmir to your home with complete trust.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              {[["100%", "Pure & Authentic"], ["5000+", "Happy Customers"], ["Pan-India", "Fast Delivery"]].map(([n, l]) => (
                <div key={l} className="rounded-2xl bg-white border border-[#E7E0D6] p-4 text-center">
                  <p className="font-display text-xl font-bold text-[#C2410C]">{n}</p>
                  <p className="text-xs text-[#6B5B52] mt-1">{l}</p>
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-flex mt-6 items-center gap-2 text-sm font-semibold text-[#C2410C] hover:gap-3 transition-all">Read our full story <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* SHIPPING */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-widest text-[#C2410C] font-semibold">Pan-India Delivery</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1F130B] mt-1">Freshness, Delivered Everywhere</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            ["Order Processing", "Orders confirmed & processed within 24 hours of placement."],
            ["Careful Packaging", "Vacuum-sealed, food-grade packaging locks in freshness."],
            ["Fast Shipping", "Dispatched via trusted couriers across all Indian pincodes."],
            ["Order Tracking", "Get tracking details on WhatsApp & email until delivery."],
          ].map(([t, d], i) => (
            <div key={t} className="rounded-2xl bg-white border border-[#E7E0D6] p-6 card-hover">
              <div className="h-11 w-11 rounded-full bg-[#FEF3C7] grid place-items-center font-display font-bold text-[#9A3412] text-lg">{i + 1}</div>
              <h3 className="font-display text-lg font-semibold text-[#1F130B] mt-4">{t}</h3>
              <p className="text-sm text-[#6B5B52] mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-[#F3ECE2]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-widest text-[#C2410C] font-semibold">Loved by Customers</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1F130B] mt-1">What Our Customers Say</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 6).map((r) => (
                <div key={r.id} className="rounded-2xl bg-white border border-[#E7E0D6] p-6">
                  <StarRating value={r.rating} size={16} />
                  <p className="mt-3 text-sm text-[#6B5B52] leading-relaxed">“{r.comment}”</p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-[#FEF3C7] grid place-items-center font-bold text-[#9A3412] text-sm">{r.name.charAt(0)}</div>
                    <p className="font-semibold text-sm text-[#1F130B]">{r.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section className="dark-luxury">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Order in seconds on WhatsApp</h2>
          <p className="mt-3 text-[#B9A794] max-w-xl mx-auto">Send us your order directly — we'll confirm, pack and ship it fresh across India.</p>
          <a href={waLink("Hi! I'd like to place an order for Kashmiri dry fruits.")} target="_blank" rel="noreferrer" className="inline-flex mt-7 items-center gap-2 rounded-full bg-[#25D366] hover:brightness-95 text-white font-semibold px-8 py-3.5 transition">
            <MessageCircle size={19} /> Chat & Order Now
          </a>
        </div>
      </section>
    </div>
  );
}
